/* ============================================================================
   EL SERVICIO — un solo motor para toda la aplicación

   ═══ POR QUÉ HAY UN SINGLETON ═══

   El motor tiene una cola y un estado en IndexedDB. Dos instancias —una por
   pantalla— competirían por la misma cola y podrían mandar el mismo cambio dos
   veces. La aplicación monta un servicio y las pantallas se suscriben.

   ═══ CUÁNDO CORRE ═══

   Al abrir la aplicación con sesión, cada minuto, al volver la conexión y al
   volver a la pestaña. No corre si no hay sesión: sin cookie, cada llamada sería
   un 401 y el motor se quedaría reintentando sin motivo.
   ========================================================================== */

import { observarCambiosDeDatos } from "../db/persistencia.js";
import { puede } from "../../domain/identidad.js";
import { almacenLocal } from "./almacenLocal.js";
import { entidadPorNombre } from "./entidades.js";
import { crearMotor, estadoVacio } from "./motor.js";
import { crearTransporte } from "./transporte.js";

export const INTERVALO_MS = 60 * 1000;

let motor = null;
let intervalo = null;
let desuscribirDatos = null;
let escuchas = [];
let montajes = 0;

function avisar(clave) {
    if (typeof window === "undefined") return;
    try {
        window.dispatchEvent(new CustomEvent("helicalc:datos-remotos", { detail: { clave } }));
    } catch { /* un navegador viejo no tiene CustomEvent; no es crítico */ }
}

function alVolverALaRed() {
    motor?.sincronizar();
}

function alCambiarVisibilidad() {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
        motor?.sincronizar();
    }
}

export function iniciarSincronizacion({ transporte = null, perfil = null } = {}) {
    if (motor) {
        montajes += 1;
        return motor;
    }
    montajes = 1;

    const permiso = (nombre, tipo) => {
        if (!perfil) return true;
        const entidad = entidadPorNombre(nombre);
        return entidad ? puede(perfil, entidad[tipo]) : false;
    };

    motor = crearMotor({
        almacen: almacenLocal,
        transporte: transporte || crearTransporte(),
        avisar,
        puedeLeer: (nombre) => permiso(nombre, "leer"),
        puedeEscribir: (nombre) => permiso(nombre, "escribir"),
    });

    desuscribirDatos = observarCambiosDeDatos(({ entidad, antes, despues }) => {
        motor.registrarDiferencia(entidad, antes, despues);
    });

    /* La primera vuelta no bloquea el arranque: la pantalla se dibuja con lo
       local y el estado del badge llega cuando el servidor contesta. */
    motor.sincronizar();
    intervalo = setInterval(() => {
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        motor.sincronizar();
    }, INTERVALO_MS);

    if (typeof window !== "undefined") {
        window.addEventListener("online", alVolverALaRed);
        document.addEventListener("visibilitychange", alCambiarVisibilidad);
        escuchas = [
            () => window.removeEventListener("online", alVolverALaRed),
            () => document.removeEventListener("visibilitychange", alCambiarVisibilidad),
        ];
    }

    return motor;
}

export function detenerSincronizacion() {
    montajes = Math.max(0, montajes - 1);
    if (montajes > 0) return;
    if (intervalo) clearInterval(intervalo);
    intervalo = null;
    desuscribirDatos?.();
    desuscribirDatos = null;
    for (const quitar of escuchas) {
        try { quitar(); } catch { /* nada que hacer */ }
    }
    escuchas = [];
    motor = null;
}

export function motorActual() {
    return motor;
}

export async function estadoDeSincronizacion() {
    if (!motor) return estadoVacio();
    return motor.estadoActual();
}

export function suscribirASincronizacion(fn) {
    return motor ? motor.suscribir(fn) : () => {};
}

export async function sincronizarAhora() {
    return motor ? motor.sincronizar() : estadoVacio();
}

export async function resolverConflictoActual(id, eleccion) {
    return motor ? motor.resolverConflicto(id, eleccion) : estadoVacio();
}

export async function conflictoPendiente() {
    return motor ? motor.conflictoActual() : null;
}

export async function rechazosPendientes() {
    return motor ? motor.rechazosActuales() : [];
}
